// Closed phrase library — applies ONLY to Register Insight (Layer 2),
// Register Trend (Layer 3), and Register Drift (Layer 4) templates. The
// main flag outputs for all three Layers live as static maps in
// toneMirrorLayers.js — NOT here.
//
// Architecture rule (per spec):
//   "LLM fills from session record only. One line. Never free-form."
//   We honor this with a closed, deterministic pick. Every line is
//   hand-tuned; nothing is generated at runtime. The "fills from session
//   record only" half is enforced by the caller passing pre-resolved
//   register / observation / trend / time_period strings — never raw
//   message text.
//
// Shape:
//   REGISTER_INSIGHT[register_a_key][register_b_key] = [phrase, phrase, ...]
//     Used by the Register Insight template (Layer 2, style_aligned + 2+
//     registers active). Compares two registers; observation phrasing is
//     baked into the lines so the closed library stays free-form-proof.
//
//   REGISTER_TREND[register_key][trend_key] = [phrase, phrase, ...]
//     Used by the Register Trend template (Layer 3, 2+ registers w/ trend
//     data). Per-register × per-trend phrasing.
//
// Trends recognized: "improving", "tension_rising", "consistent_aligned",
// "off_style", "softening", "warming". The first four mirror the Layer 3
// flag set; the last two cover register comparisons (e.g., "your work
// register has softened relative to your family register").

import { REGISTER_BY_KEY, registerPhrase } from "@/lib/registers";

// ─────────────────────────────────────────────────────────────────────────
// Register Insight (Layer 2 / style_aligned + 2+ registers)
// "Your [A] conversations are [observation] than your [B] ones lately."
// ─────────────────────────────────────────────────────────────────────────
// Keyed by [registerA][registerB] where A is the "more aligned / different"
// register and B is the comparator. Lines are full sentences for safety.
const REGISTER_INSIGHT = {
    work: {
        family: [
            "Your work conversations are running more measured than your family ones lately.",
            "Your work register has stayed steadier than your family one this stretch.",
        ],
        friend: [
            "Your work conversations are landing more composed than your friend ones lately.",
            "Your work tone is more contained than your friend register right now.",
        ],
        close: [
            "Your work conversations are more guarded than your close relationship conversations lately.",
            "Your work register feels more measured than your close relationship conversations this stretch.",
        ],
    },
    family: {
        work: [
            "Your family conversations feel warmer than your work ones lately.",
            "Your family register is running softer than your work tone this stretch.",
        ],
        friend: [
            "Your family conversations are more deliberate than your friend ones lately.",
            "Your family register is steadier than your friend one right now.",
        ],
        close: [
            "Your family conversations are running calmer than your close relationship conversations lately.",
            "Your family register is more even than your close relationship conversations this stretch.",
        ],
    },
    friend: {
        work: [
            "Your friend conversations are more open than your work ones lately.",
            "Your friend register is warmer than your work one this stretch.",
        ],
        family: [
            "Your friend conversations are running lighter than your family ones lately.",
            "Your friend register is more relaxed than your family one right now.",
        ],
        close: [
            "Your friend conversations are running breezier than your close relationship conversations lately.",
            "Your friend register is lighter than your close relationship conversations this stretch.",
        ],
    },
    close: {
        work: [
            "Your close relationship conversations are softer than your work ones lately.",
            "Your close relationship conversations are more open than your work tone this stretch.",
        ],
        family: [
            "Your close relationship conversations are running warmer than your family ones lately.",
            "Your close relationship conversations are more tender than your family ones right now.",
        ],
        friend: [
            "Your close relationship conversations are deeper than your friend ones lately.",
            "Your close relationship conversations are more intimate than your friend ones this stretch.",
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────
// Register Trend (Layer 3 / 2+ registers w/ trend data)
// "Your [register] conversations have [trend_observation] this [time_period]."
// ─────────────────────────────────────────────────────────────────────────
// Per-register × per-trend lines. The caller picks the trend that
// dominates per register and resolves a time_period elsewhere if needed —
// the closed lines already include "lately / this stretch / this week".
const REGISTER_TREND = {
    work: {
        improving: [
            "Your work conversations have grown more measured this stretch.",
            "Your work register has settled into a clearer rhythm lately.",
        ],
        tension_rising: [
            "Your work conversations have felt more pressured this stretch.",
            "Your work register has carried more tension than usual lately.",
        ],
        consistent_aligned: [
            "Your work conversations have stayed steady and composed this stretch.",
            "Your work register has held its professional baseline lately.",
        ],
        off_style: [
            "Your work conversations have drifted from your usual register this stretch.",
            "Your work register has felt less aligned with your style lately.",
        ],
    },
    family: {
        improving: [
            "Your family conversations have softened in tone this stretch.",
            "Your family register has grown gentler lately.",
        ],
        tension_rising: [
            "Your family conversations have felt sharper than usual this stretch.",
            "Your family register has carried more friction lately.",
        ],
        consistent_aligned: [
            "Your family conversations have stayed warm and steady this stretch.",
            "Your family register has held its caring baseline lately.",
        ],
        off_style: [
            "Your family conversations have felt off-key this stretch.",
            "Your family register has drifted from its usual warmth lately.",
        ],
    },
    friend: {
        improving: [
            "Your friend conversations have grown more connected this stretch.",
            "Your friend register has felt more present lately.",
        ],
        tension_rising: [
            "Your friend conversations have felt cooler than usual this stretch.",
            "Your friend register has carried more distance lately.",
        ],
        consistent_aligned: [
            "Your friend conversations have stayed easy and warm this stretch.",
            "Your friend register has held its light, open baseline lately.",
        ],
        off_style: [
            "Your friend conversations have read more formal than usual this stretch.",
            "Your friend register has felt less natural lately.",
        ],
    },
    close: {
        improving: [
            "Your close relationship conversations have grown more open this stretch.",
            "Your close relationship conversations have softened lately.",
        ],
        tension_rising: [
            "Your close relationship conversations have felt more strained this stretch.",
            "Your close relationship conversations have carried more weight lately.",
        ],
        consistent_aligned: [
            "Your close relationship conversations have stayed tender and present this stretch.",
            "Your close relationship conversations have held their warmth lately.",
        ],
        off_style: [
            "Your close relationship conversations have read more guarded than usual this stretch.",
            "Your close relationship conversations have felt less intimate lately.",
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────
// Register Drift (Layer 4 / drift_detected + identifiable source register)
// "Your [source] conversations are driving most of this shift —
//  your [aligned] messages remain aligned."
// ─────────────────────────────────────────────────────────────────────────
// Keyed by [sourceRegister][alignedRegister]. The source carries the
// drift; the aligned register stays on-style. Two variants for gentle
// rotation across renders.
const REGISTER_DRIFT = {
    work: {
        family: [
            "Your work conversations are driving most of this shift — your family messages remain aligned.",
            "Most of the drift is coming from your work register — your family conversations are still on-style.",
        ],
        friend: [
            "Your work conversations are driving most of this shift — your friend messages remain aligned.",
            "Most of the drift is coming from your work register — your friend conversations are still on-style.",
        ],
        close: [
            "Your work conversations are driving most of this shift — your close relationship conversations remain aligned.",
            "Most of the drift is coming from your work register — your close relationship conversations are still on-style.",
        ],
    },
    family: {
        work: [
            "Your family conversations are driving most of this shift — your work messages remain aligned.",
            "Most of the drift is coming from your family register — your work conversations are still on-style.",
        ],
        friend: [
            "Your family conversations are driving most of this shift — your friend messages remain aligned.",
            "Most of the drift is coming from your family register — your friend conversations are still on-style.",
        ],
        close: [
            "Your family conversations are driving most of this shift — your close relationship conversations remain aligned.",
            "Most of the drift is coming from your family register — your close relationship conversations are still on-style.",
        ],
    },
    friend: {
        work: [
            "Your friend conversations are driving most of this shift — your work messages remain aligned.",
            "Most of the drift is coming from your friend register — your work conversations are still on-style.",
        ],
        family: [
            "Your friend conversations are driving most of this shift — your family messages remain aligned.",
            "Most of the drift is coming from your friend register — your family conversations are still on-style.",
        ],
        close: [
            "Your friend conversations are driving most of this shift — your close relationship conversations remain aligned.",
            "Most of the drift is coming from your friend register — your close relationship conversations are still on-style.",
        ],
    },
    close: {
        work: [
            "Your close relationship conversations are driving most of this shift — your work messages remain aligned.",
            "Most of the drift is coming from your close relationship conversations — your work conversations are still on-style.",
        ],
        family: [
            "Your close relationship conversations are driving most of this shift — your family messages remain aligned.",
            "Most of the drift is coming from your close relationship conversations — your family conversations are still on-style.",
        ],
        friend: [
            "Your close relationship conversations are driving most of this shift — your friend messages remain aligned.",
            "Most of the drift is coming from your close relationship conversations — your friend conversations are still on-style.",
        ],
    },
};

/**
 * Deterministic pick for the Register Insight template (Layer 2).
 *
 * @param {object} args
 * @param {string} args.registerA  Primary register (the one being observed).
 * @param {string} args.registerB  Comparator register.
 * @param {number} [args.seed=0]   Stable seed (session count) so the line
 *                                  evolves slowly and the dashboard stays calm.
 * @returns {string|null}
 */
export function pickRegisterInsight({ registerA, registerB, seed = 0 }) {
    if (!REGISTER_BY_KEY[registerA] || !REGISTER_BY_KEY[registerB]) return null;
    if (registerA === registerB) return null;
    const candidates = REGISTER_INSIGHT[registerA]?.[registerB];
    if (!candidates || candidates.length === 0) return null;
    return candidates[Math.abs(seed) % candidates.length];
}

/**
 * Deterministic pick for the Register Trend template (Layer 3).
 *
 * @param {object} args
 * @param {string} args.register
 * @param {string} args.trend  One of: improving | tension_rising |
 *                              consistent_aligned | off_style.
 * @param {number} [args.seed=0]
 * @returns {string|null}
 */
export function pickRegisterTrend({ register, trend, seed = 0 }) {
    if (!REGISTER_BY_KEY[register]) return null;
    const candidates = REGISTER_TREND[register]?.[trend];
    if (!candidates || candidates.length === 0) return null;
    return candidates[Math.abs(seed) % candidates.length];
}

/**
 * Deterministic pick for the Register Drift template (Layer 4 ONLY —
 * fires when drift_detected AND register data identifies a clear source).
 *
 * @param {object} args
 * @param {string} args.sourceRegister   The register driving the drift.
 * @param {string} args.alignedRegister  The register still on-style.
 * @param {number} [args.seed=0]
 * @returns {string|null}
 */
export function pickRegisterDrift({ sourceRegister, alignedRegister, seed = 0 }) {
    if (
        !REGISTER_BY_KEY[sourceRegister] ||
        !REGISTER_BY_KEY[alignedRegister]
    ) {
        return null;
    }
    if (sourceRegister === alignedRegister) return null;
    const candidates = REGISTER_DRIFT[sourceRegister]?.[alignedRegister];
    if (!candidates || candidates.length === 0) return null;
    return candidates[Math.abs(seed) % candidates.length];
}

// Exposed for tests + future LLM-pick wiring (which must preserve the
// "session record only" guarantee documented above).
export const __internals = {
    REGISTER_INSIGHT,
    REGISTER_TREND,
    REGISTER_DRIFT,
    registerPhrase,
};
